import { Injectable, inject, signal } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { HelperNavControllerService, NavPathType } from './helper-nav-controller.service';
import { HelperDebugLoggerControllerService } from './helper-debug-logger-controller.service';
import { HelperSessionControllerService } from './helper-session-controller.service';
import { ProviderSessionService } from '../providers/gams/provider-session.service';
import { ROUTE_DIRECTION } from '../types/route.type';
import { environment } from 'src/environments/environment';

export interface PushNotificationPayload {
  title?: string;
  body?: string;
  id?: string;
  data?: Record<string, any>;
  targetRoute?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperPushNotificationControllerService {
  private navService = inject(HelperNavControllerService);
  private debugLogger = inject(HelperDebugLoggerControllerService);
  private sessionService = inject(HelperSessionControllerService);
  private providerSessionService = inject(ProviderSessionService);

  public readonly pushToken = signal<string | null>(null);
  public readonly permissionStatus = signal<'prompt' | 'granted' | 'denied'>('prompt');
  public readonly isInitialized = signal<boolean>(false);

  constructor() {
    this.init();
  }

  /**
   * Initializes push notification listeners and permission state
   */
  public async init(): Promise<void> {
    if (this.isInitialized()) {
      return;
    }

    if (!environment.allowPushNotification) {
      this.debugLogger.addLog('info', '[PUSH] Push notifications disabled in environment.');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        await this.initNativePushListeners();
      } else {
        await this.initWebNotificationState();
      }
      this.isInitialized.set(true);
    } catch (err) {
      console.warn('⚠️ Push notifications initialization error:', err);
    }
  }

  /**
   * Handles incoming notification action and deep link routing
   */
  public handleNotificationAction(payload: PushNotificationPayload): void {
    this.debugLogger.addLog('info', 'Push Notification Tapped', payload);

    const targetRoute = payload.targetRoute || payload.data?.['targetRoute'] || payload.data?.['route'];

    if (targetRoute && typeof targetRoute === 'string') {
      const cleanPath = targetRoute.replace(/^\//, '');
      this.navService.navigate(cleanPath as NavPathType, ROUTE_DIRECTION.Forward);
    }
  }

  /**
   * Synchronizes the generated FCM device push token with the backend session
   */
  public async syncTokenWithServer(token: string): Promise<void> {
    this.pushToken.set(token);
    const platform = Capacitor.getPlatform();

    this.debugLogger.addLog('info', `Push Token Registered (${platform})`, {
      tokenPreview: `${token.substring(0, 16)}...`,
      platform,
    });

    if (this.sessionService.session?.token) {
      try {
        this.providerSessionService.updatePushToken(token, platform).subscribe({
          next: () => {
            this.debugLogger.addLog('info', 'Push Token Synced with Backend', { platform });
          },
          error: (err) => {
            console.warn('⚠️ Failed to sync push token with backend:', err);
          },
        });
      } catch (err) {
        console.warn('⚠️ Exception syncing push token:', err);
      }
    }
  }

  private async initNativePushListeners(): Promise<void> {
    const PushNotifications = (Capacitor as any).Plugins?.PushNotifications;
    if (!PushNotifications) {
      return;
    }

    // Check existing permissions
    const permStatus = await PushNotifications.checkPermissions();
    this.permissionStatus.set(permStatus.receive === 'granted' ? 'granted' : 'prompt');

    // Registration Success
    PushNotifications.addListener('registration', (token: { value: string }) => {
      if (token && token.value) {
        this.syncTokenWithServer(token.value);
      }
    });

    // Registration Error
    PushNotifications.addListener('registrationError', (error: any) => {
      console.warn('⚠️ Push registration error:', error);
      this.debugLogger.addLog('error', 'Push Registration Error', error);
    });

    // Foreground Notification Received
    PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
      const payload: PushNotificationPayload = {
        id: notification.id,
        title: notification.title,
        body: notification.body,
        data: notification.data,
        targetRoute: notification.data?.['targetRoute'] || notification.data?.['route'],
      };

      this.debugLogger.addLog('info', `Push Received: ${payload.title || 'Notification'}`, payload);
    });

    // Notification Tapped / Action Performed
    PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
      const notification = action.notification;
      const payload: PushNotificationPayload = {
        id: notification?.id,
        title: notification?.title,
        body: notification?.body,
        data: notification?.data,
        targetRoute: notification?.data?.['targetRoute'] || notification?.data?.['route'],
      };

      this.handleNotificationAction(payload);
    });
  }

  private async initWebNotificationState(): Promise<void> {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const perm = Notification.permission;
      this.permissionStatus.set(perm === 'granted' ? 'granted' : perm === 'denied' ? 'denied' : 'prompt');
    }
  }
}
